import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../../components/Pill";
import { EVENT_GROUP_UI } from "../../lib/enumUI";
import { projectTimelineQueryOptions } from "../queries";
import type { TimelineEntry } from "../types";

export type EventGroup =
  | "file"
  | "payment_order"
  | "discrepancy"
  | "project"
  | "other";

function groupFor(eventType: string): EventGroup {
  const prefix = eventType.split(".")[0];
  if (prefix === "file") return "file";
  if (prefix === "payment_order") return "payment_order";
  if (prefix === "discrepancy") return "discrepancy";
  if (prefix === "project") return "project";
  return "other";
}

export function TimelineView({ projectId }: { projectId: string }) {
  const { data, isLoading, error } = useQuery(
    projectTimelineQueryOptions(projectId),
  );

  if (isLoading) {
    return (
      <div className="page">
        <TableSkeleton />
      </div>
    );
  }
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div className="page">
        <p className="alert" role="alert">
          Failed to load timeline: {message}
        </p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="page">
      <p className="page-breadcrumb">
        <Link to="/projects/$projectId" params={{ projectId }}>
          ← Project detail
        </Link>
      </p>
      <h1>Project timeline</h1>
      {data.entries.length === 0 ? (
        <p className="empty-state">
          No timeline entries recorded for this project yet.
        </p>
      ) : (
        <ol className="timeline-list">
          {data.entries.map((entry) => (
            <TimelineRow key={entry.source_id} entry={entry} />
          ))}
        </ol>
      )}
    </div>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const group = groupFor(entry.event_type);
  return (
    <li>
      <time className="timeline-time" dateTime={entry.occurred_at}>
        {new Date(entry.occurred_at).toLocaleString()}
      </time>
      <div className="timeline-body">
        <Pill tone={EVENT_GROUP_UI[group].tone}>
          {EVENT_GROUP_UI[group].label}
        </Pill>
        <code>{entry.event_type}</code>
      </div>
      <TimelinePayload payload={entry.payload} />
    </li>
  );
}

function TimelinePayload({ payload }: { payload: Record<string, unknown> }) {
  const entries = Object.entries(payload);
  if (entries.length === 0) return null;
  return (
    <details>
      <summary>details</summary>
      <pre>{JSON.stringify(payload, null, 2)}</pre>
    </details>
  );
}
